import { useState } from 'react';
import {
  useGetScimSettings,
  useGetScimTokens,
  useRevokeScimToken,
} from '@tinct/core-api';
import {
  ReadScimToken,
  SCIM_TOKENS_MAX,
  ScimPrerequisite,
} from '@tinct/core-api-models';
import { formatRelativeTime } from '@tinct/core-utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyButton,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  Skeleton,
  Switch,
} from '@tinct/web-components';
import { InfoIcon, PlusIcon, TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ScimEnablementModal } from '../../../modals/scim-enablement-modal';
import { GenerateScimTokenModal } from '../modals/generate-scim-token-modal';
import { RevokeScimTokenModal } from '../modals/revoke-scim-token-modal';
import {
  SECURITY_CARD_ANCHORS,
  SecurityCardAnchor,
} from '../security-card-anchors';
import { CardAnchorLink } from './card-anchor-link';
import { formatDate } from '../format-date';
import { ScimTokensTable } from './scim-tokens-table';
import { ScimGroupMappings } from './scim-group-mappings';

/** What each unmet prerequisite asks of the admin, and the card above where it is done. */
const PREREQUISITES: Record<
  ScimPrerequisite,
  { label: string; anchor: SecurityCardAnchor; linkLabel: string }
> = {
  no_verified_domain: {
    label: 'Verify at least one e-mail domain.',
    anchor: SECURITY_CARD_ANCHORS.ssoVerifiedDomains,
    linkLabel: 'Go to Verified domains',
  },
  connection_not_enabled: {
    label: 'Enable single sign-on.',
    anchor: SECURITY_CARD_ANCHORS.ssoStatus,
    linkLabel: 'Go to Status',
  },
};

export type ScimProvisioningCardProps = {
  organizationId: string;
  /**
   * Whether the connection still creates accounts at first sign-in; `undefined` while there is no
   * connection. On, next to SCIM, the card suggests switching it off.
   */
  jitEnabled?: boolean | null;
};

/**
 * SCIM provisioning: whether the identity provider may manage the workspace's members, the address
 * it calls, and the tokens it calls with. Rendered for the workspace's admins only (the API refuses
 * everyone else every read), inside the single sign-on section.
 */
export function ScimProvisioningCard({
  organizationId,
  jitEnabled,
}: ScimProvisioningCardProps) {
  // The state the confirmation dialog is about to apply, `null` while it is closed.
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<ReadScimToken | null>(
    null,
  );

  const {
    data: settings,
    isLoading: areSettingsLoading,
    error: settingsError,
  } = useGetScimSettings({
    organizationId,
    fields: {
      id: null,
      enabled: null,
      baseUrl: null,
      unmetPrerequisites: null,
      lastRequestAt: null,
    },
  });

  const {
    data: tokensPage,
    isLoading: areTokensLoading,
    error: tokensError,
  } = useGetScimTokens({
    staticParams: { organizationId },
    offset: 0,
    limit: SCIM_TOKENS_MAX,
    fields: {
      id: null,
      createdAt: null,
      createdBy: { fields: { id: null, firstName: null, lastName: null, email: null } },
      lastUsedAt: null,
      lastUsedIp: null,
      unused: null,
    },
  });

  const revokeToken = useRevokeScimToken({
    onSuccess: () => {
      setTokenToRevoke(null);
      toast.success('Token revoked');
    },
    onError: (error) => {
      setTokenToRevoke(null);
      toast.error(error.error);
    },
  });

  const enabled = settings?.enabled ?? false;
  const unmetPrerequisites = settings?.unmetPrerequisites ?? [];
  const tokens = tokensPage?.data ?? [];
  const hasRoomForToken = tokens.length < SCIM_TOKENS_MAX;
  // Switching on is what the prerequisites gate; switching off never is.
  const canToggle = !!settings && (enabled || unmetPrerequisites.length === 0);
  const loadError = settingsError?.error ?? tokensError?.error ?? null;

  return (
    <Card id={SECURITY_CARD_ANCHORS.scimProvisioning} tabIndex={-1}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle className="text-xl">Provisioning (SCIM)</CardTitle>
          {settings && (
            <Badge variant={enabled ? 'success' : 'secondary'}>
              {enabled ? 'On' : 'Off'}
            </Badge>
          )}
        </div>
        <CardDescription>
          Let your identity provider create, update and deactivate the members
          of this workspace from your directory.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {loadError && (
          <Alert variant="destructive">
            <TriangleAlertIcon />
            <AlertDescription>
              The SCIM settings could not be loaded. {loadError}
            </AlertDescription>
          </Alert>
        )}

        {areSettingsLoading ? (
          <Skeleton className="h-32 w-full rounded-xl" />
        ) : (
          settings && (
            <>
              {unmetPrerequisites.length > 0 && (
                <Alert variant="important">
                  <TriangleAlertIcon />
                  <AlertTitle>
                    {enabled
                      ? 'Provisioning cannot work until:'
                      : 'Before switching provisioning on:'}
                  </AlertTitle>
                  <AlertDescription>
                    <ul className="flex flex-col gap-1">
                      {unmetPrerequisites.map((prerequisite) => {
                        const copy = PREREQUISITES[prerequisite];
                        return (
                          <li key={prerequisite}>
                            {copy ? (
                              <span>
                                {copy.label}{' '}
                                <CardAnchorLink anchor={copy.anchor}>
                                  {copy.linkLabel}
                                </CardAnchorLink>
                              </span>
                            ) : (
                              prerequisite
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                  <Label htmlFor="scim-enabled" className="font-semibold">
                    SCIM provisioning
                  </Label>
                  <span className="text-sm text-muted-foreground">
                    {enabled
                      ? settings.lastRequestAt
                        ? `On. Last request from your identity provider: ${formatRelativeTime(new Date(settings.lastRequestAt))}.`
                        : 'On. Your identity provider has not called yet.'
                      : 'Off: your identity provider’s requests are refused.'}
                  </span>
                </div>
                <Switch
                  id="scim-enabled"
                  checked={enabled}
                  disabled={!canToggle}
                  onCheckedChange={setPendingEnabled}
                />
              </div>

              {enabled && jitEnabled && (
                <Alert>
                  <InfoIcon />
                  <AlertDescription>
                    <span>
                      With SCIM managing your members, consider switching off
                      account creation at first sign-in.{' '}
                      <CardAnchorLink anchor={SECURITY_CARD_ANCHORS.ssoProvisioning}>
                        Go to Provisioning
                      </CardAnchorLink>
                    </span>
                  </AlertDescription>
                </Alert>
              )}

              {settings.baseUrl && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="scim-base-url" className="font-semibold">
                    Base URL
                  </Label>
                  <InputGroup>
                    <InputGroupInput
                      id="scim-base-url"
                      readOnly
                      value={settings.baseUrl}
                      className="font-mono text-xs"
                    />
                    <InputGroupAddon align="inline-end">
                      <CopyButton
                        value={settings.baseUrl}
                        toastMessage="Base URL copied"
                      />
                    </InputGroupAddon>
                  </InputGroup>
                </div>
              )}
            </>
          )
        )}

        <div className="flex flex-col gap-3 border-t pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-semibold">Tokens</span>
              <span className="text-sm text-muted-foreground">
                To rotate without interruption, generate a second token, switch
                your identity provider to it, then revoke the first.
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              disabled={areTokensLoading || !!tokensError || !hasRoomForToken}
              onClick={() => setIsGenerateOpen(true)}
            >
              <PlusIcon className="size-4" />
              Generate token
            </Button>
          </div>

          {!areTokensLoading && !hasRoomForToken && (
            <p className="text-sm text-muted-foreground">
              A workspace holds {SCIM_TOKENS_MAX} tokens at most: revoke one
              first to generate another.
            </p>
          )}

          {areTokensLoading ? (
            <Skeleton className="h-20 w-full rounded-xl" />
          ) : tokens.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No token yet. Your identity provider needs one to call the base
              URL.
            </p>
          ) : (
            <ScimTokensTable tokens={tokens} onRevoke={setTokenToRevoke} />
          )}
        </div>

        {/* Groups only reach the workspace while the directory may call it */}
        {enabled && <ScimGroupMappings organizationId={organizationId} />}
      </CardContent>

      {pendingEnabled !== null && (
        <ScimEnablementModal
          open
          onOpenChange={(open) => !open && setPendingEnabled(null)}
          organizationId={organizationId}
          enabled={pendingEnabled}
        />
      )}

      {/* Mounted only while open: the token it shows goes with it. */}
      {isGenerateOpen && (
        <GenerateScimTokenModal
          open
          onOpenChange={setIsGenerateOpen}
          organizationId={organizationId}
        />
      )}

      {tokenToRevoke && (
        <RevokeScimTokenModal
          open
          onOpenChange={(open) => !open && setTokenToRevoke(null)}
          createdOn={formatDate(tokenToRevoke.createdAt)}
          isUnused={tokenToRevoke.unused ?? false}
          isRevoking={revokeToken.isPending}
          onConfirm={() =>
            tokenToRevoke.id &&
            revokeToken.mutate({ organizationId, tokenId: tokenToRevoke.id })
          }
        />
      )}
    </Card>
  );
}
